/**
 * Centralised route configuration — every protected route in the app
 * is declared here as a plain object. App.js maps over this array to
 * render <Route> elements, so adding a new page is a one-line change.
 *
 * Shape per entry:
 *   path        — URL pattern (react-router syntax)
 *   component   — React lazy import or direct import (injected by AppRoutes)
 *   permission  — optional VIEW_XXX string fed to <ProtectedRoute>
 *   layoutProps — optional object spread onto <Layout> (e.g. { fullWidth: true })
 *
 * Routes that DON'T follow the <ProtectedRoute><Layout> pattern
 * (login, signup, auth callback, access-denied) are defined directly
 * in App.js — they're one-offs and don't benefit from the config.
 */

// ── Page imports ──────────────────────────────────────────
import Home from "./pages/Home/Home";
import Users from "./pages/Users/Users";
import TradeRecommendations from "./pages/TradeRecommendations/TradeRecommendations";
import Grievances from "./pages/Grievances/Grievances.jsx";
import News from "./pages/News/NewsForm.jsx";
import GrievanceResponse from "./components/grievances/GrievanceResponse/GrievaceResponse.jsx";
import InvoiceList from "./pages/Invoice/InvoiceList.jsx";
import PaymentGatewayList from "./pages/Payments/PaymentGatewayList.jsx";
import ProPlanTransactionsList from "./pages/ProPlanTransactions/ProPlanTransactionsList.jsx";
import AddInvoice from "./pages/Invoice/AddInvoice.jsx";
import AddPaymentGateway from "./pages/Payments/AddPaymentGateway.jsx";
import AddProPlanTransaction from "./pages/ProPlanTransactions/AddProPlanTransaction.jsx";
import OfferList from "./pages/Offers/OfferList.jsx";
import AddOffer from "./pages/Offers/AddOffer.jsx";
import LearningModuleMaster from "./components/learning/LearningModuleMaster.jsx";
import AddLearningModuleMaster from "./components/learning/AddLearningModuleMaster.jsx";
import ChapterMaster from "./components/chaptermaster/ChapterMaster.jsx";
import AddChapterMaster from "./components/chaptermaster/AddChapterMaster.jsx";
import LearningModules from "./components/learningmodules/LearningModules.jsx";
import AddLearningModules from "./components/learningmodules/AddLearningModules.jsx";
import AddGrievanceResponse from "./components/grievances/GrievanceResponse/AddGrievanceResponse.jsx";
import NewsFeed from "./pages/News/NewsFeed.jsx";
import AddGrievances from "./pages/Grievances/AddGrievances.jsx";
import InternalTokenList from "./pages/InternalTokenList/InternalTokenList.jsx";
import IndicesControl from "./pages/IndicesControl/IndicesControl.jsx";
import RedFlag from "./pages/RedFlag/RedFlag.jsx";
import CommunityMaster from "./pages/CommunityMaster/CommunityMaster.jsx";
import Profile from "./pages/Profile/Profile.jsx";
import NewsCategoryList from "./pages/NewsCategory/NewsCategoryList.jsx";
import EducationFeed from "./pages/Education/EducationFeed.jsx";

import EducationCategoryList from "./pages/EducationCategory/EducationCategoryList.jsx";
import UserCompliance from "./components/users/UserCompliance/UserCompliance.jsx";
import UserKycPage from "./pages/Users/UserKycPage/UserKycPage.jsx";
import UserDocumentsPage from "./pages/Users/UserDocumentsPage/UserDocumentsPage.jsx";
import UserDetailsPage from "./pages/Users/UserDetailsPage/UserDetailsPage.jsx";
import ControlTradingDays from "./pages/ControlTradingDays/ControlTradingDays.jsx";
import Notifications from "./components/Notifications/Notifications.jsx";
import AddNotifications from "./components/Notifications/AddNotifications.jsx";
import AccessRights from "./components/AccessControl/AccessRights.jsx";
import NotificationQuery from "./components/NotificationQuery/NotificationQuery.jsx";
import AddNotificationQuery from "./components/NotificationQuery/AddNotificationQuery.jsx";
import AccessRoles from "./components/AccessControl/AccessRoles/AccessRoles.jsx";
import AccessTypes from "./components/AccessControl/AccessTypes/AccessTypes.jsx";
import AddAccessRoles from "./components/AccessControl/AccessRoles/AddAccessRoles.jsx";
import AddAccessTypes from "./components/AccessControl/AccessTypes/AddAccessTypes.jsx";
import AddAccessRights from "./components/AccessControl/AddAccessRights.jsx";
import IndustryMaster from "./pages/IndustryMaster/IndustriesMaster.jsx";
import SectorMaster from "./pages/SectorMaster/SectorMaster.jsx";
import MarketDataImport from "./pages/MarketDataImport/MarketDataImport.jsx";
import EquityMarketCap from "./pages/EquityMarketCap/EquityMarketCap.jsx";
import DealsImport from "./pages/DealsImport/DealsImport.jsx";
import GrossDeliverables from "./pages/GrossDeliverables/GrossDeliverables.jsx";
import FiftyTwoWeekHigh from "./pages/FiftyTwoWeekHigh/FiftyTwoWeekHigh.jsx";
import FiftyTwoWeekLow from "./pages/FiftyTwoWeekLow/FiftyTwoWeekLow.jsx";
import FIIData from "./pages/FIIData/FIIData.jsx";
import FIIFOData from "./pages/FIIFOData/FIIFOData.jsx";
import PERatio from "./pages/PERatio/PERatio.jsx";
import BlockKeyword from "./pages/BlockKeyword/BlockKeyword.jsx";
import PlansList from "./pages/PlansManagement/PlansManagement.jsx";
import Strategies from "./pages/Strategies/Strategies";
import StrategyDetail from "./pages/Strategies/StrategyDetail";
import BacktestPage from "./pages/Strategies/BacktestPage";
import Signals from "./pages/Signals/Signals";
import StockDetailsPage from "./pages/StockDetails/StockDetailsPage";
import EmailNotification from "./pages/EmailNotification/EmailNotification";
import SmsNotification from "./pages/SmsNotification/SmsNotification";

// ── Route config ──────────────────────────────────────────
// Each entry maps 1-to-1 to a <Route> wrapped in
// <ProtectedRoute><Layout>. Order matches the old App.js
// so git-diff stays readable.
const routeConfig = [
  // ── Dashboard ─────────────────────────────────────
  { path: "/home", component: Home },

  // ── Users ─────────────────────────────────────────
  { path: "/users", component: Users, permission: "VIEW_USERSLIST" },
  { path: "/users/compliance", component: UserCompliance, permission: "VIEW_USERSLIST" },
  { path: "/users/kyc", component: UserKycPage, permission: "VIEW_USERKYC" },
  { path: "/users/documents", component: UserDocumentsPage, permission: "VIEW_USERDOCUMENT" },
  { path: "/users/details", component: UserDetailsPage, permission: "VIEW_USERSLIST" },

  // ── Block Keyword ─────────────────────────────────
  { path: "/block-keyword", component: BlockKeyword, permission: "VIEW_BLOCK_KEYWORD" },

  // ── Trade Recommendations ─────────────────────────
  { path: "/traderecommendations", component: TradeRecommendations, permission: "VIEW_TRADERECOMMENDATIONS" },

  // ── Stock Details ─────────────────────────────────
  { path: "/stock-details", component: StockDetailsPage },


  // ── Grievances ────────────────────────────────────
  { path: "/grievances", component: Grievances, permission: "VIEW_GRIEVANCE" },

  // ── News ──────────────────────────────────────────
  { path: "/newsfeed", component: NewsFeed, permission: "VIEW_NEWSFEED" },
  { path: "/news_category", component: NewsCategoryList, permission: "VIEW_NEWSCATEGORY" },

  // ── Learning ──────────────────────────────────────
  { path: "/learning", component: LearningModuleMaster, permission: "VIEW_LEARNINGMODULEMASTER" },
  { path: "/learning/add", component: AddLearningModuleMaster },
  { path: "/learning/add/:id", component: AddLearningModuleMaster },
  { path: "/chaptermaster", component: ChapterMaster, permission: "VIEW_CHAPTERMASTER" },
  { path: "/chaptermaster/add", component: AddChapterMaster },
  { path: "/chaptermaster/add/:id", component: AddChapterMaster },
  { path: "/learningmodule", component: LearningModules, permission: "VIEW_LEARNINGCONTENT" },
  { path: "/learningmodule/add", component: AddLearningModules },
  { path: "/learningmodule/add/:id", component: AddLearningModules },

  // ── Internal Tokens & Indices ─────────────────────
  { path: "/internal-tokens", component: InternalTokenList, permission: "VIEW_INTERNALTOKEN_INDICESCONTROL" },
  { path: "/indices-control", component: IndicesControl, permission: "VIEW_INTERNALTOKEN_INDICESCONTROL" },

  // ── Industry & Sector ─────────────────────────────
  { path: "/industry-master", component: IndustryMaster, permission: "VIEW_INDUSTRY_MASTER" },
  { path: "/sector-master", component: SectorMaster, permission: "VIEW_SECTOR_MASTER" },

  // ── Strategy Engine ───────────────────────────────
  { path: "/strategies", component: Strategies, permission: "VIEW_STRATEGYENGINE" },
  { path: "/strategies/:id", component: StrategyDetail, permission: "VIEW_STRATEGYENGINE" },
  { path: "/strategies/:id/backtest", component: BacktestPage, permission: "VIEW_STRATEGYENGINE", layoutProps: { fullWidth: true } },
  { path: "/signals", component: Signals, permission: "VIEW_STRATEGYENGINE" },

  // ── Trading Days ──────────────────────────────────
  { path: "/control-tradingdays", component: ControlTradingDays, permission: "VIEW_CONTROLTRADING" },

  // ── Market Data ───────────────────────────────────
  { path: "/market_import_data", component: MarketDataImport, permission: "VIEW_MARKETDATA_IMPORT" },
  { path: "/equity_market_cap", component: EquityMarketCap, permission: "VIEW_MARKETDATA_IMPORT" },
  { path: "/deals_import", component: DealsImport, permission: "VIEW_MARKETDATA_IMPORT" },
  { path: "/fii_data", component: FIIData, permission: "VIEW_MARKETDATA_IMPORT" },
  { path: "/fii_fo_data", component: FIIFOData, permission: "VIEW_MARKETDATA_IMPORT" },
  { path: "/gross_deliverables", component: GrossDeliverables, permission: "VIEW_MARKETDATA_IMPORT" },
  { path: "/fifty_two_week_high", component: FiftyTwoWeekHigh, permission: "VIEW_MARKETDATA_IMPORT" },
  { path: "/fifty_two_week_low", component: FiftyTwoWeekLow, permission: "VIEW_MARKETDATA_IMPORT" },
  { path: "/pe_ratio", component: PERatio, permission: "VIEW_MARKETDATA_IMPORT" },

  // ── Education ─────────────────────────────────────
  { path: "/education_category", component: EducationCategoryList, permission: "VIEW_EDUCATIONCATEGORY" },
  { path: "/educationfeed", component: EducationFeed, permission: "VIEW_EDUCATIONFEED" },


  // ── Red Flag ──────────────────────────────────────
  { path: "/redflag", component: RedFlag, permission: "VIEW_REDFLAG" },

  // ── Community ─────────────────────────────────────
  { path: "/community-master", component: CommunityMaster, permission: "VIEW_EXPLORE" },

  // ── Profile ───────────────────────────────────────
  { path: "/profile", component: Profile },

  // ── Notifications ─────────────────────────────────
  { path: "/notifications", component: Notifications, permission: "VIEW_NOTIFICATIONS" },

  // ── Email Notifications ────────────────────────────────
  { path: "/email-notifications", component: EmailNotification, permission: "VIEW_NOTIFICATIONS" },

  // ── SMS Notifications ─────────────────────────────────
  { path: "/sms-notifications", component: SmsNotification, permission: "VIEW_SMSNOTIFICATION" },

  // ── Access Control ────────────────────────────────
  { path: "/access-rights", component: AccessRights, permission: "VIEW_ACCESSCONTROL" },
  { path: "/access-roles", component: AccessRoles, permission: "VIEW_ACCESSCONTROL" },
  { path: "/access-types", component: AccessTypes, permission: "VIEW_ACCESSCONTROL" },

  // ── Notification Query ────────────────────────────
  { path: "/notification-query", component: NotificationQuery, permission: "VIEW_NOTIFICATIONQUERY" },

  // ── Plans ─────────────────────────────────────────
  { path: "/plans", component: PlansList, permission: "VIEW_PLANS" },

  // ── Invoices ──────────────────────────────────────
  { path: "/invoice", component: InvoiceList, permission: "VIEW_INVOICE" },
  { path: "/invoice/add/:id", component: AddInvoice },

  // ── Pro Plan Transactions ─────────────────────────
  { path: "/pro-plan-transactions", component: ProPlanTransactionsList, permission: "VIEW_PRO_PLAN_TRANSACTIONS" },

  // ── Payment Gateway ───────────────────────────────
  { path: "/payment-gateway", component: PaymentGatewayList, permission: "VIEW_PAYMENT_GATEWAY" },

  // ── Offers ────────────────────────────────────────
  { path: "/offers", component: OfferList, permission: "VIEW_OFFERS" },
];

export default routeConfig;
